// Microsoft (Azure) OAuth through Supabase Auth.
//
// Access control is a short allowlist: the `allowed_users` table in the
// database, enforced by RLS. After Supabase returns a session, we check
// whether the user's email has a row there; if not, we sign them back out
// and show a friendly "not authorised" screen. The real gate is the RLS
// policies — the client check just exists so the user sees a clean message
// instead of empty queries.

import { useState, useEffect } from "react";
import { supabase } from "./supabase.js";

export function useSession() {
  const [state, setState] = useState({ loading: true, session: null });
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setState({ loading: false, session: data.session });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active) setState({ loading: false, session });
    });
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, []);
  return state;
}

export async function signInWithMicrosoft() {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "azure",
    options: {
      scopes: "email openid profile",
      redirectTo: window.location.origin,
    },
  });
  if (error) throw error;
}

export async function signOut() {
  await supabase.auth.signOut();
}

// Returns true if the signed-in user has a row in allowed_users.
// Relies on an RLS policy that only returns the user's own row, so a
// non-empty result means the user is allowed.
export async function isUserAllowed() {
  const { data, error } = await supabase
    .from("allowed_users")
    .select("email")
    .limit(1);
  if (error) return false;
  return (data || []).length > 0;
}

export function userEmail(session) {
  return session?.user?.email || "";
}

export function userInitials(session) {
  const meta = session?.user?.user_metadata || {};
  const name = meta.name || meta.full_name || "";
  if (name) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase() || "?";
  }
  const email = userEmail(session);
  return email ? email[0].toUpperCase() : "?";
}
